import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import {
  iNotificationSettingsDto,
  NotificationRight,
  NotificationRights,
} from 'src/app/helpers/Interfaces/notification-settings';
import { environment } from 'src/environments/environment.development';

@Injectable({
  providedIn: 'root',
})
export class NotificationSettingsService extends BaseService {
  private readonly notifications = `${environment.base_url}/api/Notifications`;
  constructor() {
    super();
  }
  add(payload: iNotificationSettingsDto) {
    delete payload.id;
    const idRecur = (x: NotificationRight[]) => {
      x.forEach((y) => {
        delete y.id;
        idRecur(y.notificationRight);
      });
    };
    payload.rights.forEach((x) => {
      delete x.id;
      idRecur(x.notificationRight);
    });
    return this._http.post<iNotificationSettingsDto>(
      `${this.notifications}`,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  removeNullableFields<T>(obj: T): T {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      const filtered = obj
        .map((item) => this.removeNullableFields(item))
        .filter((item) => item !== null && item !== undefined);

      // Return null for empty arrays if you want to completely remove them
      return (filtered.length === 0 ? null : filtered) as T;
    }

    if (typeof obj === 'object') {
      const cleaned: any = {};

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = (obj as any)[key];
          const cleanedValue = this.removeNullableFields(value);

          // Only keep the key if the cleaned value is not null/undefined
          // (or if it's a boolean false, 0, or empty string — these are valid)
          if (
            cleanedValue !== null &&
            cleanedValue !== undefined &&
            // Keep false, 0, empty string — these are intentional
            !(
              typeof cleanedValue === 'object' &&
              Object.keys(cleanedValue).length === 0
            )
          ) {
            cleaned[key] = cleanedValue;
          }
        }
      }

      // If object is empty after cleaning, return null (or {} if preferred)
      const result = Object.keys(cleaned).length === 0 ? null : cleaned;
      return result as T;
    }

    // For primitives (string, number, boolean)
    return obj;
  }
  update(payload: iNotificationSettingsDto) {
    const value = this.removeNullableFields(payload);
    return this._http.put<iNotificationSettingsDto>(
      `${this.notifications}/${payload.id}`,
      value,
      {
        responseType: 'json',
      }
    );
  }
  get() {
    return this._http.get<iNotificationSettingsDto[]>(this.notifications, {
      responseType: 'json',
    });
  }
  getFilter(designationId = '', employeeId = '') {
    return this._http.get<iNotificationSettingsDto[]>(
      `${this.notifications}/filter`,
      {
        responseType: 'json',
        params: { designationId, employeeId },
      }
    );
  }
  getById(id: string) {
    return this._http.get<iNotificationSettingsDto>(
      `${this.notifications}/${id}`,
      {
        responseType: 'json',
      }
    );
  }
  delete(id: string) {
    return this._http.delete(`${this.notifications}/${id}`, {
      responseType: 'json',
    });
  }
  getByWorkspaceId(isSystemadmin: boolean, alt: boolean = false) {
    return this._http
      .get<iNotificationSettingsDto[]>(
        `${this.notifications}/GetByWorkspaceId`,
        {
          responseType: 'json',
          params: {
            workspaceId: this._dataServ.employeeValue.workspaceId || '',
          },
        }
      )
      .pipe(
        map((res) => this.mergeNotificationSettings(res, isSystemadmin, alt))
      );
  }
  getIsDefault() {
    return this._http.get<iNotificationSettingsDto>(
      `${this.notifications}/GetIsDefault`,
      {
        responseType: 'json',
      }
    );
  }
  // mergeNotificationSettings(
  //   settings: iNotificationSettingsDto[],
  //   isSystemadmin: boolean,
  //   alt: boolean
  // ): iNotificationSettingsDto | null {
  //   if (settings.length === 0) return null;

  //   // Case 1: Single setting (must be default)
  //   if (settings.length === 1) {
  //     const defaultSettings = settings[0];
  //     if (!defaultSettings.isDefault) {
  //       throw new Error('Single setting must be default settings');
  //     }

  //     const result = this.deepCloneSettings(defaultSettings);
  //     if (!isSystemadmin && !alt) {
  //       this.stripIds(result);
  //     }
  //     return result;
  //   }

  //   if (settings.length !== 2) {
  //     throw new Error('Expected one or two notification settings objects');
  //   }

  //   const defaultSettings = settings.find((s) => s.isDefault);
  //   const customSettings = settings.find((s) => !s.isDefault);

  //   if (!defaultSettings) {
  //     throw new Error('Missing default settings');
  //   }

  //   if (!customSettings) {
  //     const result = this.deepCloneSettings(defaultSettings);
  //     if (!isSystemadmin && !alt) {
  //       this.stripIds(result);
  //     }
  //     return result;
  //   }

  //   const merged: iNotificationSettingsDto =
  //     this.deepCloneSettings(defaultSettings);
  //   if (customSettings.id) merged.id = customSettings.id;

  //   // Use a safe key: fallback to empty string for null to avoid Map collisions
  //   const getModuleKey = (id: string | null): string => id ?? '';

  //   // Build map of default modules using safe key
  //   const defaultModuleMap = new Map<string, NotificationRight>();
  //   defaultSettings.rights.forEach((module) => {
  //     defaultModuleMap.set(getModuleKey(module.notificationRightId), module);
  //   });

  //   const mergedRights: NotificationRight[] = [];

  //   // Process custom modules
  //   customSettings.rights.forEach((customModule) => {
  //     const key = getModuleKey(customModule.notificationRightId);
  //     const defaultModule = defaultModuleMap.get(key);

  //     let mergedModule: NotificationRight;

  //     if (!defaultModule) {
  //       // New module not in default → take custom fully
  //       mergedModule = { ...customModule };
  //     } else if (defaultModule.ignore) {
  //       // Default has ignore: true → keep default (but allow custom id)
  //       mergedModule = { ...defaultModule };
  //       if (customModule.id) mergedModule.id = customModule.id;
  //     } else {
  //       // Merge: custom overrides non-ignore properties
  //       mergedModule = {
  //         ...defaultModule,
  //         ...customModule,
  //         id: customModule.id ?? defaultModule.id,
  //         enable: customModule.hasOwnProperty('enable')
  //           ? customModule.enable
  //           : defaultModule.enable,
  //         ignore: customModule.ignore ?? defaultModule.ignore,
  //         notificationRightId:
  //           customModule.notificationRightId ??
  //           defaultModule.notificationRightId,
  //         notificationRight: this.mergeChildRights(
  //           defaultModule.notificationRight,
  //           customModule.notificationRight
  //         ),
  //       };
  //     }

  //     mergedRights.push(mergedModule);
  //   });

  //   // Add default modules that weren't overridden (and not ignored)
  //   defaultSettings.rights.forEach((defaultModule) => {
  //     const key = getModuleKey(defaultModule.notificationRightId);
  //     const wasOverridden = customSettings.rights.some(
  //       (c) => getModuleKey(c.notificationRightId) === key
  //     );

  //     if (!wasOverridden && !defaultModule.ignore) {
  //       mergedRights.push({ ...defaultModule });
  //     }
  //   });

  //   merged.rights = mergedRights;

  //   if (!isSystemadmin && !alt) {
  //     this.stripIds(merged);
  //   }

  //   return merged;
  // }
  mergeNotificationSettings(
    settings: iNotificationSettingsDto[],
    isSystemadmin: boolean,
    alt: boolean
  ): iNotificationSettingsDto | null {

    if (!settings || settings.length === 0) return null;

    if (settings.length === 1) {
      const single = this.deepCloneSettings(settings[0]);
      if (!isSystemadmin && !alt) this.stripIds(single);
      return single;
    }

    const defaultSettings = settings.find(s => s.isDefault) || null;
    const customList = settings.filter(s => !s.isDefault);

    const effectiveDefault = defaultSettings
      ? this.deepCloneSettings(defaultSettings)
      : this.deepCloneSettings(settings[0]);

    if (customList.length === 0) {
      const result = this.deepCloneSettings(effectiveDefault);
      if (!isSystemadmin && !alt) this.stripIds(result);
      return result;
    }

    const effectiveCustom = this.deepCloneSettings(customList[customList.length - 1]);

    const merged: iNotificationSettingsDto = this.deepCloneSettings(effectiveDefault);

    if (effectiveCustom.id) merged.id = effectiveCustom.id;

    const getKey = (id: string | null) => id ?? "__null__";

    const defaultModuleMap = new Map<string, NotificationRight>();
    effectiveDefault.rights.forEach(mod =>
      defaultModuleMap.set(getKey(mod.notificationRightId), mod)
    );

    const mergedRights: NotificationRight[] = [];

    effectiveCustom.rights.forEach(customMod => {
      const key = getKey(customMod.notificationRightId);
      const base = defaultModuleMap.get(key);

      if (!base) {
        mergedRights.push(this.deepCloneSettings(customMod));
        return;
      }

      if (base.ignore) {
        const finalMod = this.deepCloneSettings(base);
        if (customMod.id) finalMod.id = customMod.id;
        mergedRights.push(finalMod);
        return;
      }

      const mergedMod: NotificationRight = {
        ...this.deepCloneSettings(base),
        ...this.deepCloneSettings(customMod),
        id: customMod.id ?? base.id,
        enable: customMod.hasOwnProperty("enable") ? customMod.enable : base.enable,
        ignore: customMod.ignore ?? base.ignore,
        notificationRightId: customMod.notificationRightId ?? base.notificationRightId,
        notificationRight: this.mergeChildRights(
          base.notificationRight,
          customMod.notificationRight
        )
      };

      mergedRights.push(mergedMod);
    });

    effectiveDefault.rights.forEach(base => {
      const key = getKey(base.notificationRightId);
      const overridden = effectiveCustom.rights.some(
        cm => getKey(cm.notificationRightId) === key
      );

      if (!overridden && !base.ignore) {
        mergedRights.push(this.deepCloneSettings(base));
      }
    });

    merged.rights = mergedRights;

    if (!isSystemadmin && !alt) {
      this.stripIds(merged);
    }

    return merged;
  }

  // Helper: safe deep clone
  // private deepCloneSettings(
  //   settings: iNotificationSettingsDto
  // ): iNotificationSettingsDto {
  //   return structuredClone
  //     ? structuredClone(settings)
  //     : JSON.parse(JSON.stringify(settings));
  // }
  private deepCloneSettings<T>(data: T): T {
    if (typeof structuredClone === 'function') {
      return structuredClone(data);
    }

    if (data === null || typeof data !== 'object') return data;

    if (data instanceof Date) return new Date(data.getTime()) as T;

    if (Array.isArray(data)) {
      return data.map(item => this.deepCloneSettings(item)) as T;
    }

    const cloned: any = {};
    for (const key of Object.keys(data)) {
      cloned[key] = this.deepCloneSettings((data as any)[key]);
    }

    return cloned as T;
  }


  // Merge child rights (same as before, but safer with null checks)
  private mergeChildRights(
    defaultRights: NotificationRight[],
    customRights: NotificationRight[]
  ): NotificationRight[] {
    const result: NotificationRight[] = [];
    const defaultMap = new Map<string, NotificationRight>();
    defaultRights.forEach((r) => {
      if (r.rightsId) defaultMap.set(r.rightsId, r);
    });

    customRights.forEach((customRight) => {
      const defaultRight = customRight.rightsId
        ? defaultMap.get(customRight.rightsId)
        : undefined;

      if (!defaultRight) {
        result.push({ ...customRight });
        return;
      }

      if (defaultRight.ignore) {
        result.push({ ...defaultRight });
        return;
      }

      const mergedSendTo = {
        ...defaultRight.sendTo,
        ...customRight.sendTo,
        specificDesignations: {
          ids:
            customRight.sendTo.specificDesignations?.ids &&
              customRight.sendTo.specificDesignations.ids.length > 0
              ? customRight.sendTo.specificDesignations.ids
              : defaultRight.sendTo.specificDesignations.ids,
        },
        specificUsers: {
          ids:
            customRight.sendTo.specificUsers?.ids &&
              customRight.sendTo.specificUsers.ids.length > 0
              ? customRight.sendTo.specificUsers.ids
              : defaultRight.sendTo.specificUsers.ids,
        },
      };

      result.push({
        ...defaultRight,
        ...customRight,
        sendTo: mergedSendTo,
      });
    });

    // Add missing default rights
    defaultRights.forEach((defaultRight) => {
      if (!customRights.some((r) => r.rightsId === defaultRight.rightsId)) {
        result.push({ ...defaultRight });
      }
    });

    return result;
  }

  // Strip all IDs recursively
  private stripIds(settings: iNotificationSettingsDto): void {
    settings.id = null;
    settings.workspaceId = null;

    const strip = (right: NotificationRight) => {
      right.id = null;
      right.notificationRight.forEach(strip);
    };

    settings.rights.forEach(strip);
  }
}
